/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { Accordion, AccordionHeader, AccordionPanel } from '@coursera/cds-core';

import type { FilterOption } from 'bundles/enterprise-admin-drawers/components/types';
import ActivityFilterDateClause from 'bundles/enterprise-admin-user-filters/components/ActivityFilterDateClause';
import type {
  UserFilterBuilderError,
  UserFilterBuilderErrors,
} from 'bundles/enterprise-admin-user-filters/components/ErrorsNotification';
import { EnrolledInCoursesAccordion } from 'bundles/enterprise-admin-user-filters/components/activity-filters/common/Accordions';
import { SubSection } from 'bundles/enterprise-admin-user-filters/components/ui';
import type { ActivityFilter } from 'bundles/enterprise-admin-user-filters/types';
import { getDays } from 'bundles/enterprise-admin-user-filters/utils';
import { DebouncedTextField, Spacer } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

type CompletedProps = {
  errors: UserFilterBuilderErrors;
  selectedCourses: FilterOption[];
  activityFilter: ActivityFilter;
  onCoursesSelected: (selectedCourses: FilterOption[]) => void;
  onActivityFilterChange: (activityFilter: ActivityFilter) => void;
  onClearError: (error: UserFilterBuilderError) => void;
};

type CompletedAtLeastOneProps = {
  errors: UserFilterBuilderErrors;
  activityFilter: ActivityFilter;
  onActivityFilterChange: (activityFilter: ActivityFilter) => void;
  onClearError: (error: UserFilterBuilderError) => void;
};

const CompletedAtLeastOneCourseAccordion = ({
  errors,
  activityFilter,
  onActivityFilterChange,
  onClearError,
}: CompletedAtLeastOneProps) => {
  const days = getDays(activityFilter?.completionDate);
  return (
    <Accordion variant="silent" id="numberOfDaysSinceCompletion" defaultExpanded>
      <AccordionHeader
        label={_t(`Completed at least 1 course in the last #{days} days`, {
          days,
        })}
      />
      <AccordionPanel>
        <DebouncedTextField
          label={_t('Last completion date within (days)')}
          type="number"
          defaultValue={String(days ?? '')}
          onChange={(value) => {
            onActivityFilterChange({
              ...activityFilter,
              completionDate: { type: 'RELATIVE', nDaysAgoAfter: Number(value) },
            });
            onClearError('numberOfDaysSinceCompletion');
          }}
          data-testid="completed_at_least_one_course"
          validationLabel={errors.numberOfDaysSinceCompletion}
          validationStatus={errors.numberOfDaysSinceCompletion ? 'error' : undefined}
        />
      </AccordionPanel>
    </Accordion>
  );
};

const CompletedActivityFilter = ({
  errors,
  selectedCourses,
  activityFilter,
  onCoursesSelected,
  onActivityFilterChange,
  onClearError,
}: CompletedProps) => (
  <>
    <SubSection>
      <EnrolledInCoursesAccordion selectedCourses={selectedCourses} onCoursesSelected={onCoursesSelected} />
    </SubSection>
    <Spacer size={16} />
    <SubSection>
      <ActivityFilterDateClause activityFilter={activityFilter} onActivityFilterChange={onActivityFilterChange} />
    </SubSection>
    <Spacer size={16} />
    <SubSection>
      <CompletedAtLeastOneCourseAccordion
        errors={errors}
        activityFilter={activityFilter}
        onActivityFilterChange={onActivityFilterChange}
        onClearError={onClearError}
      />
    </SubSection>
  </>
);

export default CompletedActivityFilter;
